import { minLength, requiredIf } from '@vuelidate/validators';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isValidIIN } from '@aviata/iin';
import type { ComputedRef } from 'vue';
import { citizenshipEntity } from 'shared';

dayjs.extend(customParseFormat);

import { validationI18n } from '../../../entities/validation-i18n';
import { validationRulesLib } from '../../../shared/lib';

/**
 * @description The 7th digit in IIN always tells the century in which person was born
 *
 * @see https://yvision.kz/post/iin-algoritm-rascheta-i-proverki-114938
 */
const IIN_BORN_CENTURY = {
  twentieth: [3, 4],
  twentyFirst: [5, 6],
};

const isValidDocumentExpireDate = (value: string, departureDate?: string) => {
  if (departureDate) {
    const formattedDate = dayjs(value, 'DD.MM.YYYY', true);

    return (
      value === '' ||
      (validationRulesLib.isValidDate(value) &&
        validationRulesLib.isNotExpired(formattedDate, departureDate))
    );
  }
  return value === '' || validationRulesLib.isValidDate(value);
};

const isValidDocumentGivenAtDate = (value: string, departureDate?: string) => {
  if (departureDate) {
    const formattedDate = dayjs(value, 'DD.MM.YYYY', true);

    return (
      value === '' ||
      (validationRulesLib.isValidDate(value) &&
        validationRulesLib.isAfterOrEqual(formattedDate, departureDate))
    );
  }
  return value === '' || validationRulesLib.isValidDate(value);
};

const isValidIinDate = (iin: string) => {
  const datePart = iin.slice(0, 6);

  if (!/^\d{6}$/.test(datePart)) {
    return false;
  }

  const year = parseInt(iin.slice(0, 2), 10);
  const month = iin.slice(2, 4);
  const day = iin.slice(4, 6);
  const bornInCenturyId = parseInt(iin.slice(6, 7), 10);

  let fullYear = 2000 + year;

  // year 2000 is in 20th century, so we skip it
  if (year !== 0 && IIN_BORN_CENTURY.twentieth.includes(bornInCenturyId)) {
    fullYear = 1900 + year;
  }

  const parsed = dayjs(`${fullYear}-${month}-${day}`, 'YYYY-MM-DD', true);

  return parsed.isValid();
};

/**
 * Base passenger validations with i18n support
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
 */
export const getBaseValidations = (
  citizenship: ComputedRef<string>,
  departureDate: ComputedRef<string | undefined> | undefined,
  messagePath: string,
) => {
  const { required, withI18nMessage } =
    validationI18n.model.useLocalizedValidators(messagePath);
  /**
   * Need to use method since validators does not reflect reactive property change.
   * Validators call every function that is passed to it on every value update
   */
  const isIinRequired = () =>
    citizenship.value === citizenshipEntity.config.KAZAKHSTAN_CODE;

  return {
    firstName: {
      required,
      alpha: withI18nMessage(validationRulesLib.isAlphaHyphen),
    },
    lastName: {
      required,
      alpha: withI18nMessage(validationRulesLib.isAlphaHyphen),
    },
    gender: { required },
    dob: {
      required,
      isValidDob: withI18nMessage(validationRulesLib.isValidDate),
    },
    citizenship: { required },
    document: {
      number: {
        required,
        alphaNumCyrillic: withI18nMessage(
          validationRulesLib.isAlphaNumCyrillic,
        ),
        docMinLength: withI18nMessage(minLength(5)),
      },
      expiresAt: {
        isValidDocExpiration: withI18nMessage((value: string) =>
          isValidDocumentExpireDate(value, departureDate?.value),
        ),
      },
    },
    iin: {
      required: withI18nMessage(requiredIf(isIinRequired)),
      iinMinLength: withI18nMessage(
        (value: string) => !isIinRequired() || value.length === 12,
      ),
      isInvalidIin: withI18nMessage(
        (value: string) =>
          !isIinRequired() || (isValidIIN(value) && isValidIinDate(value)),
      ),
    },
  };
};

export const getBaseValidationsForSMProvider = (
  citizenship: ComputedRef<string>,
  departureDate: ComputedRef<string | undefined> | undefined,
  messagePath: string,
) => {
  const { required, withI18nMessage } =
    validationI18n.model.useLocalizedValidators(messagePath);
  /**
   * Need to use method since validators does not reflect reactive property change.
   * Validators call every function that is passed to it on every value update
   */
  const isFinRequired = () =>
    citizenship.value === citizenshipEntity.config.AZERBAIJAN_CODE;
  const isSeriesRequired = () =>
    !citizenshipEntity.config.countriesWithoutSeries.includes(
      citizenship.value,
    );

  return {
    document: {
      expiresAt: {
        required,
        isValidDocExpiration: withI18nMessage((value: string) =>
          isValidDocumentExpireDate(value, departureDate?.value),
        ),
      },
      givenAt: {
        required,
        isValidDocGivenAt: withI18nMessage((value: string) =>
          isValidDocumentGivenAtDate(value, departureDate?.value),
        ),
      },
      organization: {
        required,
        alpha: withI18nMessage(validationRulesLib.isAlphaNumSpace),
      },
      series: {
        required: withI18nMessage(requiredIf(isSeriesRequired)),
        isValidSeries: withI18nMessage(
          (value: string) =>
            !isSeriesRequired() ||
            validationRulesLib.isValidDocumentSeries(value),
        ),
      },
    },
    fin: {
      required: withI18nMessage(requiredIf(isFinRequired)),
      finMinLength: withI18nMessage(
        (value: string) => !isFinRequired() || value.length === 7,
      ),
    },
  };
};
